import type { Licence, PersistedStripePrice, SanitizedStripeSource, Site } from '@sine/sine-core-api-types';
import type { ISelectOption } from '@sine/sine-ui-components';

import type { SiteStatistics } from '../reporting/reporting.types';

export interface BillingSite
  extends Pick<Site, 'id' | 'name' | 'settings' | 'address' | 'status' | 'countryCode' | 'created' | 'owner' | 'tags'> {
  licence?: Licence;
}

export type Country = 'us' | 'au' | 'nz' | 'gb' | 'eu';
export type Currency = 'usd' | 'aud' | 'nzd' | 'gbp' | 'eur';

export enum BillingModeType {
  OFF = 'off',
  CREDIT_CARD = 'credit_card',
  PURCHASE_ORDER = 'purchase_order',
  MIXED = 'mixed',
}

export enum BillingCycle {
  YEARLY = 'year',
  MONTHLY = 'month',
  DAILY = 'day',
}

export enum CardBrand {
  VISA = 'Visa',
  MASTERCARD = 'Mastercard',
  AMERICAN_EXPRESS = 'American Express',
  UNKNOWN = 'Unknown',
}

export enum LicenseType {
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'enterprise',
  ENTERPRISE = 'unlimited',
  TRIAL_30_DAYS = 'trial-30-days',
  TRIAL_SYSADMIN = 'trial-sysadmin',
  /**
   * @deprecated Use TRIAL_30_DAYS instead
   */
  TRIAL_BASIC = 'trial-basic',
  /**
   * Overlaps with `StripePlanType.LARGE`
   * @deprecated Use `LARGE` instead.
   */
  ENT_100 = 'ent100',

  VMSLITE = 'vmslite',
  VMSLITE_CMSLITE = 'vmslite_cmslite',
  VMSLITE_CMSPLUS = 'vmslite_cmsplus',
  VMSPLUS = 'vmsplus',
  VMSPLUS_CMSLITE = 'vmsplus_cmslite',
  VMSPLUS_CMSPLUS = 'vmsplus_cmsplus',
  VMSPREMIUM = 'vmspremium',
  VMSPREMIUM_CMSLITE = 'vmspremium_cmslite',
  VMSPREMIUM_CMSPLUS = 'vmspremium_cmsplus',
}

export interface BadgeInfo {
  badgeName: string;
  count: number;
  licenseIds: Array<string>;
  subscriptionId: string;
}

export interface Card extends SanitizedStripeSource {
  default: boolean;
}

export type RefreshableOnCloseFn = (refresh?: boolean) => void;

export interface PlanSelectOption extends ISelectOption {
  price?: PersistedStripePrice;
  subscriptionId?: string;
  count?: number;
  type?: string;
  licenseIds?: Array<string>;
  siteId?: string | null;
}

export interface HydratedBillableSite extends BillingSite, SiteStatistics {
  /**
   * Used only by the forms to track which sites were added before sending to API.
   */
  isNew?: true;
}

export interface LineItem {
  site: HydratedBillableSite | Partial<Site> | undefined;
  selectedPlanOption?: PlanSelectOption;
}
